import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import heroRailroad from '../assets/hero-railroad.jpg';

const RailroadPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Railroad Vegetation Management BC | Boultbee</title>
            <meta name="description" content="High-rail herbicide application for railroads in BC. Ballast weed control, right-of-way management, crossing visibility, and GPS-tracked precision application." />
            <link rel="canonical" href="https://www.weedmanager.com/industries/railroad" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Railroad Vegetation Management BC | Boultbee" />
            <meta property="og:description" content="High-rail herbicide application for railroads in BC. Ballast weed control, right-of-way management, crossing visibility, and GPS-tracked precision application." />
            <meta property="og:url" content="https://www.weedmanager.com/industries/railroad" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-railroad.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Railroad Vegetation Management BC | Boultbee" />
            <meta name="twitter:description" content="High-rail herbicide application for railroads in BC. Ballast weed control, right-of-way management, crossing visibility, and GPS-tracked precision application." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-railroad.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Railroad Vegetation Management", "item": "https://www.weedmanager.com/industries/railroad" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Railroad Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
                "description": "High-rail equipped herbicide application for railroad ballast weed control, right-of-way management, yard vegetation elimination, and crossing visibility in British Columbia."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Railroad Vegetation Management"
            subtitle="Specialized herbicide application for rail corridors across British Columbia"
            backgroundImage={heroRailroad}
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee provides railroad vegetation management with hi-rail equipped spray vehicles, GPS-tracked precision application, and decades of mainline and short-line experience across BC.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Ballast & Right-of-Way Weed Control</h2>
              <p className="text-gray-700">Uncontrolled vegetation in rail ballast retains moisture, accelerates tie deterioration, and compromises track stability. Our ballast herbicide programs use pre-emergent and post-emergent applications timed to the growing season to deliver season-long vegetation control. We treat mainline corridors, branch lines, spur tracks, and yard areas using hi-rail equipped spray vehicles that operate directly on track. GPS tracking ensures precise, consistent coverage across every section of track.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Yard & Facility Vegetation Elimination</h2>
              <p className="text-gray-700">Rail yards, intermodal facilities, maintenance shops, and marshalling areas require bare ground conditions for operational safety. Our bare ground programs combine residual herbicides with targeted post-emergent treatments to maintain vegetation-free surfaces around switches, signals, crossing equipment, and fueling areas. We work within railroad safety protocols and carry all required certifications for track access.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Crossing Visibility & Signal Line Clearance</h2>
              <p className="text-gray-700">Vegetation at grade crossings and around signal equipment creates safety hazards and regulatory violations. Boultbee provides targeted herbicide treatment for crossing sight lines, signal mast foundations, cable strips, and communication equipment pads.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Industries We Serve */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Industries We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/industries/oil-gas" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2C12 2 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-5-11-5-11Z" /></svg>
                  <h3 className="font-bold text-gray-900">Oil & Gas</h3>
                  <p className="text-gray-700 text-sm mt-1">Refineries, pipelines & tank farms</p>
                </Link>
                <Link to="/industries/municipal" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V8.25l-9-6.75-9 6.75V21h4.5" /></svg>
                  <h3 className="font-bold text-gray-900">Municipalities</h3>
                  <p className="text-gray-700 text-sm mt-1">Parks, sidewalks & public spaces</p>
                </Link>
                <Link to="/industries/utility" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg>
                  <h3 className="font-bold text-gray-900">Utilities</h3>
                  <p className="text-gray-700 text-sm mt-1">Substations, corridors & cable strips</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Boultbee delivers proven railroad herbicide programs for mainline and short-line operations across BC. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default RailroadPage;
